import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { workOrdersApi, tecniciApi, committentiApi } from '../services/api'
import { Link } from 'react-router-dom'
import { format } from 'date-fns'
import { it } from 'date-fns/locale'
import { RefreshCw, MapPin, Clock, Search, X } from 'lucide-react'

const STATO_COLOR: Record<string, { bg: string; text: string; label: string; chip: string }> = {
  ricevuto:  { bg: '#f1f5f9', text: '#64748b', label: 'Ricevuto',  chip: '#e2e8f0' },
  assegnato: { bg: '#eff6ff', text: '#1d4ed8', label: 'Assegnato', chip: '#bfdbfe' },
  sospeso:   { bg: '#fffbeb', text: '#b45309', label: 'Sospeso',   chip: '#fde68a' },
  eseguito:  { bg: '#f0fdf4', text: '#15803d', label: 'Eseguito',  chip: '#bbf7d0' },
  annullato: { bg: '#fef2f2', text: '#b91c1c', label: 'Annullato', chip: '#fecaca' },
}

const ALL_STATI = Object.keys(STATO_COLOR)

function coloreColonna(wos: any[]): string {
  if (wos.length === 0) return '#f8fafc'
  const ora = new Date()
  if (wos.some(w => w.sla_chiusura && new Date(w.sla_chiusura) < ora && w.stato !== 'eseguito')) return '#fef2f2'
  if (wos.every(w => w.stato === 'eseguito' || w.stato === 'annullato')) return '#f0fdf4'
  return '#f8fafc'
}

function bordoColonna(wos: any[]): string {
  if (wos.length === 0) return '#e2e8f0'
  const ora = new Date()
  if (wos.some(w => w.sla_chiusura && new Date(w.sla_chiusura) < ora && w.stato !== 'eseguito')) return '#fca5a5'
  if (wos.every(w => w.stato === 'eseguito' || w.stato === 'annullato')) return '#86efac'
  return '#e2e8f0'
}

export default function DispatcherBoard() {
  const oggi = format(new Date(), 'yyyy-MM-dd')

  // Filtri server-side
  const [search, setSearch]               = useState('')
  const [tecnicoFilter, setTecnicoFilter] = useState('')
  const [committenteFilter, setCommittenteFilter] = useState('')
  const [slaViolato, setSlaViolato]       = useState(false)

  // Filtri client-side
  const [dataFrom, setDataFrom]           = useState(oggi)
  const [dataTo, setDataTo]               = useState(oggi)
  const [regioneFilter, setRegioneFilter] = useState('')
  const [filtriStato, setFiltriStato]     = useState<Set<string>>(new Set(ALL_STATI))

  function toggleStato(stato: string) {
    setFiltriStato(prev => {
      const next = new Set(prev)
      if (next.has(stato)) { if (next.size > 1) next.delete(stato) }
      else next.add(stato)
      return next
    })
  }

  const tuttiAttivi = filtriStato.size === ALL_STATI.length
  function toggleTutti() {
    setFiltriStato(tuttiAttivi ? new Set(['ricevuto']) : new Set(ALL_STATI))
  }

  const hasExtraFilters = !!(tecnicoFilter || committenteFilter || slaViolato || regioneFilter || dataFrom !== oggi || dataTo !== oggi)

  function resetFiltri() {
    setSearch('')
    setTecnicoFilter('')
    setCommittenteFilter('')
    setSlaViolato(false)
    setRegioneFilter('')
    setDataFrom(oggi)
    setDataTo(oggi)
    setFiltriStato(new Set(ALL_STATI))
  }

  // Query server-side
  const serverParams = {
    per_page: 9999,
    tipo_intervento: 'standard',
    ...(search && { search }),
    ...(tecnicoFilter && { tecnico_id: Number(tecnicoFilter) }),
    ...(committenteFilter && { committente_id: Number(committenteFilter) }),
    ...(slaViolato && { sla_violato: true }),
  }

  const { data: rawData, isLoading, refetch } = useQuery({
    queryKey: ['dispatcher-board', serverParams],
    queryFn: () => workOrdersApi.list(serverParams).then(r => r.data),
    refetchInterval: 60_000,
  })

  const allWos: any[] = rawData?.items ?? []

  // Filtri client-side: data + regione + stato chip
  const wos = allWos.filter(w => {
    if (dataFrom && (!w.data_appuntamento || w.data_appuntamento < dataFrom)) return false
    if (dataTo   && (!w.data_appuntamento || w.data_appuntamento > dataTo))   return false
    if (regioneFilter && (w.cliente_regione ?? '') !== regioneFilter) return false
    if (!filtriStato.has(w.stato ?? 'ricevuto')) return false
    return true
  })

  // Conteggio per stato (sui WO post-data/regione, pre-chip)
  const wosPreChip = allWos.filter(w => {
    if (dataFrom && (!w.data_appuntamento || w.data_appuntamento < dataFrom)) return false
    if (dataTo   && (!w.data_appuntamento || w.data_appuntamento > dataTo))   return false
    if (regioneFilter && (w.cliente_regione ?? '') !== regioneFilter) return false
    return true
  })
  const conteggioPerStato: Record<string, number> = {}
  ALL_STATI.forEach(s => { conteggioPerStato[s] = 0 })
  wosPreChip.forEach(w => { if (w.stato in conteggioPerStato) conteggioPerStato[w.stato]++ })

  // Regioni disponibili (dai dati caricati)
  const regioniDisponibili = [...new Set(allWos.map((w: any) => w.cliente_regione).filter(Boolean))].sort() as string[]

  // Raggruppa per tecnico
  const grouped: Record<string, { tecnico: any; wos: any[] }> = {}
  const noTecnico: any[] = []
  wos.forEach(w => {
    if (!w.tecnico_id) { noTecnico.push(w); return }
    const key = String(w.tecnico_id)
    if (!grouped[key]) grouped[key] = { tecnico: { id: w.tecnico_id, fullName: w.tecnico_nome || '?' }, wos: [] }
    grouped[key].wos.push(w)
  })
  const colonne = Object.values(grouped)
  if (noTecnico.length > 0) colonne.push({ tecnico: { id: 0, fullName: 'Non Assegnato' }, wos: noTecnico })

  // Select options
  const { data: tecniciData } = useQuery({
    queryKey: ['tecnici-bulk'],
    queryFn: () => tecniciApi.list({ per_page: 100 }).then(r => r.data.items),
  })
  const tecnici: any[] = tecniciData ?? []

  const { data: committentiData } = useQuery({
    queryKey: ['committenti-select'],
    queryFn: () => committentiApi.list({ per_page: 100 }).then(r => r.data.items),
  })
  const committenti: any[] = committentiData ?? []

  const labelData = dataFrom === dataTo
    ? format(new Date(dataFrom + 'T12:00:00'), 'EEEE d MMMM yyyy', { locale: it })
    : `${format(new Date(dataFrom + 'T12:00:00'), 'd MMM', { locale: it })} – ${format(new Date(dataTo + 'T12:00:00'), 'd MMM yyyy', { locale: it })}`

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="page-title">Dispatcher Board</h1>
          <p className="page-subtitle">
            {labelData}
            {' · '}
            {wos.length} interventi
            {allWos.length !== wos.length && ` (su ${allWos.length} totali)`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => { setDataFrom(oggi); setDataTo(oggi) }} className="btn-secondary text-sm">Oggi</button>
          <button onClick={() => refetch()} className="btn-ghost p-2" title="Aggiorna">
            <RefreshCw size={15} style={{ color: '#64748b' }} />
          </button>
        </div>
      </div>

      {/* Barra filtri */}
      <div className="card p-4 space-y-3">
        {/* Riga 1 — search + select */}
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-48">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              className="input pl-9"
              placeholder="Cerca numero, comune, denominazione..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <select className="select w-40" value={tecnicoFilter} onChange={e => setTecnicoFilter(e.target.value)}>
            <option value="">Tutti i tecnici</option>
            {tecnici.map((t: any) => (
              <option key={t.id} value={t.id}>{t.nome} {t.cognome}</option>
            ))}
          </select>
          <select className="select w-44" value={committenteFilter} onChange={e => setCommittenteFilter(e.target.value)}>
            <option value="">Tutti i committenti</option>
            {committenti.map((c: any) => (
              <option key={c.id} value={c.id}>{c.ragione_sociale}</option>
            ))}
          </select>
          <select className="select w-40" value={regioneFilter} onChange={e => setRegioneFilter(e.target.value)}>
            <option value="">Tutte le regioni</option>
            {regioniDisponibili.map(r => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
          <button
            onClick={() => setSlaViolato(v => !v)}
            className="btn-secondary text-xs flex items-center gap-1.5 whitespace-nowrap"
            style={slaViolato ? { borderColor: '#dc2626', color: '#dc2626', background: '#fef2f2' } : {}}
          >
            ⚠ SLA violato{slaViolato ? ' ✕' : ''}
          </button>
          {(search || hasExtraFilters) && (
            <button onClick={resetFiltri} className="btn-ghost text-xs flex items-center gap-1" title="Azzera tutti i filtri">
              <X size={12} /> Reset
            </button>
          )}
        </div>

        {/* Riga 2 — date range */}
        <div className="flex flex-wrap items-center gap-3">
          <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>Appuntamento:</span>
          <input
            type="date"
            className="input w-36 text-sm"
            title="Dal"
            value={dataFrom}
            onChange={e => { setDataFrom(e.target.value); if (e.target.value > dataTo) setDataTo(e.target.value) }}
          />
          <span style={{ fontSize: 12, color: '#94a3b8' }}>→</span>
          <input
            type="date"
            className="input w-36 text-sm"
            title="Al"
            value={dataTo}
            onChange={e => { setDataTo(e.target.value); if (e.target.value < dataFrom) setDataFrom(e.target.value) }}
          />
        </div>

        {/* Riga 3 — chip stato */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>Stato:</span>
          <button
            onClick={toggleTutti}
            style={{
              padding: '4px 10px', borderRadius: 99, fontSize: 11, fontWeight: 700, cursor: 'pointer', border: '1.5px solid',
              background: tuttiAttivi ? '#1E40AF' : '#f1f5f9',
              color: tuttiAttivi ? '#fff' : '#64748b',
              borderColor: tuttiAttivi ? '#1E40AF' : '#e2e8f0',
              transition: 'all 0.15s',
            }}
          >
            Tutti ({wosPreChip.length})
          </button>
          {ALL_STATI.map(stato => {
            const s = STATO_COLOR[stato]
            const attivo = filtriStato.has(stato)
            const count = conteggioPerStato[stato]
            return (
              <button
                key={stato}
                onClick={() => toggleStato(stato)}
                style={{
                  padding: '4px 10px', borderRadius: 99, fontSize: 11, fontWeight: 700, cursor: 'pointer', border: '1.5px solid',
                  background: attivo ? s.chip : '#f8fafc',
                  color: attivo ? s.text : '#94a3b8',
                  borderColor: attivo ? s.chip : '#e2e8f0',
                  opacity: count === 0 ? 0.45 : 1,
                  transition: 'all 0.15s',
                }}
              >
                {s.label} {count > 0 && `(${count})`}
              </button>
            )
          })}
        </div>
      </div>

      {/* Board */}
      {isLoading ? (
        <div className="card p-12 text-center text-slate-400 text-sm">Caricamento...</div>
      ) : colonne.length === 0 ? (
        <div className="card p-12 text-center">
          <p className="text-slate-500 font-medium">Nessun intervento trovato con i filtri selezionati</p>
          <p className="text-slate-400 text-sm mt-1">Modifica i filtri o cambia il range di date</p>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 14, overflowX: 'auto', paddingBottom: 8, alignItems: 'flex-start' }}>
          {colonne.map(({ tecnico, wos: tWos }) => {
            const bgCol = coloreColonna(tWos)
            const bordo = bordoColonna(tWos)
            const parole = tecnico.fullName.split(' ').filter(Boolean)
            const iniziali = parole.slice(0, 2).map((p: string) => p[0]).join('').toUpperCase()
            const completati = tWos.filter(w => w.stato === 'eseguito').length
            return (
              <div key={tecnico.id} style={{
                minWidth: 270, maxWidth: 300, flex: '0 0 270px',
                background: bgCol, borderRadius: 14,
                border: `2px solid ${bordo}`,
                display: 'flex', flexDirection: 'column', gap: 0,
              }}>
                {/* Intestazione colonna */}
                <div style={{ padding: '12px 14px 10px', borderBottom: `1px solid ${bordo}` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                      background: tecnico.id === 0 ? '#e2e8f0' : '#1E40AF',
                      color: tecnico.id === 0 ? '#94a3b8' : '#fff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 13, fontWeight: 700,
                    }}>
                      {iniziali}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 13, color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {tecnico.fullName}
                      </div>
                      <div style={{ fontSize: 11, color: '#64748b' }}>
                        {completati}/{tWos.length} completati
                      </div>
                    </div>
                  </div>
                </div>

                {/* Card WO */}
                <div style={{ padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {tWos.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '12px 0', fontSize: 11, color: '#94a3b8' }}>
                      Nessun intervento
                    </div>
                  ) : tWos.map(w => {
                    const s = STATO_COLOR[w.stato] ?? STATO_COLOR.ricevuto
                    const inRitardo = w.sla_chiusura && new Date(w.sla_chiusura) < new Date() && w.stato !== 'eseguito'
                    return (
                      <Link key={w.id} to={`/pratiche/${w.id}`} style={{ textDecoration: 'none' }}>
                        <div
                          style={{
                            background: '#fff', borderRadius: 10, padding: '10px 12px',
                            border: `1px solid ${inRitardo ? '#fca5a5' : '#e5e7eb'}`,
                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                            transition: 'box-shadow 0.15s',
                          }}
                          onMouseEnter={e => (e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)')}
                          onMouseLeave={e => (e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)')}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700, color: '#1E40AF' }}>
                              #{w.numero_progressivo}
                            </span>
                            <span style={{ background: s.chip, color: s.text, padding: '2px 7px', borderRadius: 99, fontSize: 10, fontWeight: 700 }}>
                              {s.label}
                            </span>
                          </div>
                          <div style={{ fontSize: 12, fontWeight: 600, color: '#111827', marginBottom: 4, lineHeight: 1.3 }}>
                            {w.denominazione_sito || w.cliente_comune || '—'}
                          </div>
                          {w.cliente_comune && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#64748b' }}>
                              <MapPin size={11} style={{ flexShrink: 0 }} />
                              {w.cliente_comune}{w.cliente_regione ? ` (${w.cliente_regione})` : ''}
                            </div>
                          )}
                          {w.data_appuntamento && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#64748b', marginTop: 3 }}>
                              <Clock size={11} style={{ flexShrink: 0 }} />
                              {format(new Date(w.data_appuntamento + 'T12:00:00'), 'dd/MM', { locale: it })}
                              {w.slot_orario && ` · ${w.slot_orario}`}
                            </div>
                          )}
                          {inRitardo && (
                            <div style={{ marginTop: 5, fontSize: 10, fontWeight: 700, color: '#dc2626', background: '#fef2f2', borderRadius: 6, padding: '2px 6px', display: 'inline-block' }}>
                              ⚠ SLA violato
                            </div>
                          )}
                        </div>
                      </Link>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
